/**
 * CallQualityIndicator - "is it the bridge, or is it me?"
 *
 * On a recovery bridge that question gets asked out loud every few minutes, and answering it
 * wrong is expensive: people repeat themselves, or worse, decide the person who keeps
 * breaking up is unreliable rather than on a bad link. Chime measures this already -
 * useMediaStreamMetrics is fed by the WebRTC stats report - so this is a read-out, not a new
 * measurement, and it costs nothing.
 *
 * Upstream loss and round-trip time are the two that matter to the person looking at it:
 * loss is what makes them unintelligible to everyone else, round trip is what makes the
 * conversation talk over itself. Bandwidth is deliberately not shown - it is the number
 * people misread as "my internet is fine".
 *
 * WHY THIS DOES NOT TRUST A ZERO
 * ------------------------------
 * `useMediaStreamMetrics` starts every field at null and then, on the first
 * `metricsDidReceive`, writes every one of them through `| 0`
 * (amazon-chime-sdk-component-library-react 3.12, useMediaStreamMetrics). That coercion turns
 * NaN and undefined into 0, so after the first report a metric that was never measured is
 * indistinguishable from a metric measured at zero - and the version of this component that
 * read `loss ?? fallback` and `rtt ?? fallback` therefore showed a green "Good" on a call
 * where nothing was measurable at all. A quality indicator that reports Good when it knows
 * nothing is worse than no indicator: it is the thing that ends the "is it me?" conversation
 * with the wrong answer.
 *
 * Two consequences, both deliberate:
 *
 *  - Nullish fallbacks are gone. `audioUpstreamRoundTripTimeMs ?? currentRoundTripTimeMs`
 *    could never select the fallback, because the SDK writes both in the same setState;
 *    `audioPacketsReceivedFractionLossPercent` was doubly dead - unreachable for the same
 *    reason, and always 0 anyway, being a 0-1 fraction truncated by `| 0` despite the
 *    "Percent" in its name. Selection is now on whether a value is a real measurement.
 *  - A grade is only ever reported once a round trip has actually been measured (rtt > 0).
 *    Until then this says so. Real WebRTC round trips are not zero; a zero here means the
 *    report carried no round trip for us, so there is nothing to grade and the loss figure
 *    alongside it has not earned any trust either.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaStreamMetrics } from 'amazon-chime-sdk-component-library-react';
import './VideoCall.css';

// Chosen to match what is actually audible on a voice bridge rather than a network SLA:
// 3% upstream loss is where words start disappearing, 5% is where people ask you to repeat.
// The SDK truncates loss to an integer, so these compare whole percent.
const POOR_LOSS_PERCENT = 5;
const FAIR_LOSS_PERCENT = 3;
const POOR_RTT_MS = 400;
const FAIR_RTT_MS = 250;

/** The first of these that is a real, positive measurement, or null if none of them is. */
function firstMeasured(...values) {
    for (const value of values) {
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
    }
    return null;
}

function CallQualityIndicator() {
    const { t } = useTranslation();
    const {
        audioPacketsSentFractionLossPercent,
        audioUpstreamRoundTripTimeMs,
        currentRoundTripTimeMs
    } = useMediaStreamMetrics();

    // Upstream round trip first - it is the one that describes this end's link - falling back
    // to the connection's round trip only when upstream carried no measurement.
    const rtt = firstMeasured(audioUpstreamRoundTripTimeMs, currentRoundTripTimeMs);

    // Loss is only meaningful alongside a round trip we believe: 0 is both "no loss" and "not
    // measured", and only the presence of a real rtt tells the two apart.
    const loss = rtt != null
        && typeof audioPacketsSentFractionLossPercent === 'number'
        && Number.isFinite(audioPacketsSentFractionLossPercent)
        ? audioPacketsSentFractionLossPercent
        : null;

    const { level, label } = useMemo(() => {
        // Nothing measured yet - the first stats report is a second or two into the call, and
        // some calls never produce a round trip for this end at all. Say so rather than
        // showing a grade nobody has earned.
        if (rtt == null) {
            return { level: 'unknown', label: t('aiChat.calls.connectionUnknown') };
        }
        if ((loss != null && loss >= POOR_LOSS_PERCENT) || rtt >= POOR_RTT_MS) {
            return { level: 'poor', label: t('aiChat.calls.connectionPoor') };
        }
        if ((loss != null && loss >= FAIR_LOSS_PERCENT) || rtt >= FAIR_RTT_MS) {
            return { level: 'fair', label: t('aiChat.calls.connectionFair') };
        }
        return { level: 'good', label: t('aiChat.calls.connectionGood') };
    }, [loss, rtt, t]);

    // The numbers go in the tooltip, not the bar: the bar is for glancing at mid-sentence.
    const detail = rtt != null
        ? t('aiChat.calls.connectionDetail', {
            rtt: Math.round(rtt),
            loss: loss != null ? loss.toFixed(1) : '-'
        })
        : label;

    return (
        <span
            className={`call-quality call-quality-${level}`}
            title={detail}
            aria-label={`${t('aiChat.calls.connection')}: ${label}`}
        >
            <span className="call-quality-bars" aria-hidden="true">
                <span className="call-quality-bar" />
                <span className="call-quality-bar" />
                <span className="call-quality-bar" />
            </span>
            <span className="call-quality-label">{label}</span>
        </span>
    );
}

export default CallQualityIndicator;
